import { PrivateURLConstants } from '@constants/private-url.constant';
import {
  getNextPageParam,
  useReactQueryCaching,
} from '@hooks/use-react-query-caching';
import { Application } from '@models/application.model';
import { Page } from '@models/page';
import {
  APPLICATIONS_INFINITE_QUERY_KEY,
  APPLICATIONS_QUERY_KEY,
} from '@modules/project-switcher/constants';
 
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import api from '../api/instance';
import { logErrorToSentry } from '@utils/common';

const nameSpace = PrivateURLConstants.APPLICATIONS;

const findAll = (query, page = 0) => {
  return api
    .get(nameSpace, { query, page })
    .then((res) => new Page<Application>().deserialize(res, Application))
    .catch((e) => Promise.reject(e));
};

const show = (applicationId: number) => {
  return api
    .get(nameSpace + '/' + applicationId)
    .then((res) => new Application().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const update = (applicationId: number, application: Application) => {
  return api
    .put(nameSpace + '/' + applicationId, application)
    .then((res) => new Application().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const create = (payload: Application) => {
  return api
    .post(nameSpace, payload)
    .then((res) => new Application().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const remove = (applicationId: number) => {
  return api
    .remove(nameSpace + '/' + applicationId)
    .then((res) => console.log(res))
    .catch((err) => logErrorToSentry({ error: err }));
};

const ApplicationService = {
  findAll,
  show,
  update,
  create,
  remove,
};

export default ApplicationService;

export const useQueryShowApplication = ({
  applicationId,
  enabled = true,
  onSuccess = (res: Application) => {},
}) => {
  const { getCachingTime } = useReactQueryCaching(
    APPLICATIONS_QUERY_KEY,
    false,
  );
  return useQuery({
    queryKey: [APPLICATIONS_QUERY_KEY, applicationId],
    queryFn: async () => await show(applicationId),
    placeholderData: new Application(),
    staleTime: getCachingTime(),
    cacheTime: getCachingTime(),
    enabled,
    onSuccess,
  });
};

export const useApplicationsFindAll = ({
  query,
  enabled = true,
  onError = console.error,
  onSuccess = (res) => {},
}) => {
  return useQuery({
    queryKey: [APPLICATIONS_QUERY_KEY, query],
    queryFn: () => findAll(query),
    enabled: enabled,
    onError,
    onSuccess,
  });
};

export const useFetchApplications = ({
  filterQuery = '',
  queryKey = [],
  keepPreviousData = true,
  enabled = true,
}) => {
  const { getCachingTime } = useReactQueryCaching(
    APPLICATIONS_INFINITE_QUERY_KEY,
    false,
  );

  return useInfiniteQuery({
    queryKey: [APPLICATIONS_INFINITE_QUERY_KEY, ...queryKey, filterQuery],
    queryFn: ({ pageParam = 0 }) => findAll(filterQuery, pageParam),
    getNextPageParam,
    staleTime: getCachingTime(),
    cacheTime: getCachingTime(),
    keepPreviousData,
    enabled,
  });
};

const useRefetchAllApplicationsCache = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.refetchQueries(APPLICATIONS_INFINITE_QUERY_KEY);
    queryClient.refetchQueries(APPLICATIONS_QUERY_KEY);
  };
};

export const useCreateApplicationMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchAllApplicationsCache = useRefetchAllApplicationsCache();

  return useMutation(create, {
    onSuccess: (data) => {
      onSuccess(data);
      refetchAllApplicationsCache();
    },
    onError,
    onSettled,
  });
};

export const useDeleteApplicationMutation = ({
  onSuccess = () => {},
  onError = console.error,
}) => {
  const refetchAllApplicationsCache = useRefetchAllApplicationsCache();

  return useMutation(remove, {
    onSuccess: () => {
      onSuccess();
      refetchAllApplicationsCache();
    },
    onError,
  });
};

export const useUpdateApplicationMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
}) => {
  const refetchAllApplicationsCache = useRefetchAllApplicationsCache();

  return useMutation(
    ({ applicationId, application }) => {
      return update(applicationId, application);
    },
    {
      onSuccess: (data) => {
        onSuccess(data);
        refetchAllApplicationsCache();
      },
      onError,
    },
  );
};
